// 파일 전송(설계서 §9.4 · v2 §3.11) — **통합앱(Tauri) 네이티브 창구**. 짝 = `src-tauri/src/files.rs`.
//
// 브라우저와 다른 점:
//  - 보낼 출처: 탐색기에서 복사한 파일(Ctrl+V → OS 파일 클립보드) · 창에 끌어다 놓은 파일(Tauri drag-drop).
//    웹뷰는 이 파일들의 내용을 읽을 수 없어서 네이티브가 목록을 만들고, **JS 는 핸들 + 순번으로 조각만** 받는다.
//  - 받을 곳: 앱 스테이징 → 전부 받으면 네이티브가 **내 파일 클립보드(MOVE)** 에 올린다 → 탐색기에서 Ctrl+V.
// 채널(`channel.ts`)은 출처·받을 곳이 네이티브인지 모른 채 `FileSource`/`FileSink` 로만 쓴다.
import type { LocalPane } from './localPane';
import { FILE_ERR, FileError, joinPath, parseListing, type DirItem, type Entry, type Listing } from './protocol';
import type { FileSink } from './sink';
import type { FileSource } from './source';

/** 끌어다 놓는 중(창 위를 지나감·벗어남) — 🔴 files.rs `FILE_DRAG_EVENT` 와 같아야 한다. */
export const FILE_DRAG_EVENT = 'rs-file-drag';
/** 파일을 놓았다(목록 수집 끝 → 핸들) — 🔴 files.rs `FILE_DROP_EVENT` 와 같아야 한다. */
export const FILE_DROP_EVENT = 'rs-file-drop';
/** 바이너리 본문 쓰기 요청 머리 — 🔴 files.rs `HDR_HANDLE`·`HDR_INDEX` 와 같아야 한다. */
const HDR_HANDLE = 'x-rs-handle';
const HDR_INDEX = 'x-rs-index';
/** 네이티브 명령 이름 — 🔴 lib.rs `generate_handler!` 의 `files::*` 와 같아야 한다. */
const CMD = {
  CLIP_READ: 'files_clip_read',
  READ: 'files_read',
  RELEASE: 'files_release',
  SINK_BEGIN: 'files_sink_begin',
  SINK_WRITE: 'files_sink_write',
  /** 받기 공유 버퍼(Windows) — 만들어 달라 · 채운 만큼 써 달라. */
  SINK_SHM: 'files_sink_shm',
  SINK_COMMIT: 'files_sink_commit',
  SINK_FINISH: 'files_sink_finish_file',
  SINK_COMPLETE: 'files_sink_complete',
  REVEAL: 'files_reveal',
  /** 끌어내기 3단계 — 받은 것을 네이티브 끌기로 내보낸다. */
  DRAG_START: 'files_drag_start',
  // 파일 패널(§9.4) 내 PC 칸 — 목록·폴더 조작은 경로로, 내용 읽기·쓰기는 여전히 핸들·연 폴더로만.
  LOCAL_HOME: 'files_local_home',
  LOCAL_LIST: 'files_local_list',
  LOCAL_MKDIR: 'files_local_mkdir',
  LOCAL_DELETE: 'files_local_delete',
  LOCAL_SOURCE: 'files_local_source',
  LOCAL_SINK_BEGIN: 'files_local_sink_begin',
  LOCAL_SINK_COMPLETE: 'files_local_sink_complete',
} as const;
/**
 * 받은 조각(32KB)을 모았다가 네이티브에 한 번에 넘기는 크기. 조각마다 부르면 1GB 에 3만 번 — 호출 부담이 속도를 좌우한다
 * (브라우저 DirectorySink 의 FLUSH_BYTES 와 같은 이유·같은 값).
 */
const FLUSH_BYTES = 1024 * 1024;
/**
 * 네이티브 출처가 한 번에 읽는 크기 — 읽기 한 번이 IPC 왕복이라 크게 읽는다(실측 2026-09-15: 1 MiB 29.7 MB/s → 4 MiB 65 MB/s).
 * 🔴 files.rs `MAX_READ_BYTES` 이하여야 한다(넘으면 네이티브가 잘라 준다 — 동작은 하지만 왕복이 늘어난다).
 */
const NATIVE_READ_BLOCK = 4 * 1024 * 1024;
/** 공유 버퍼 메타데이터의 핸들 키 — 🔴 files.rs `SHM_META_KEY` 와 같아야 한다. */
const SHM_META_KEY = 'rsSink';
/** WebView2 가 공유 버퍼를 스크립트에 건네는 이벤트 이름(WebView2 규약). */
const SHM_EVENT = 'sharedbufferreceived';
/** 공유 버퍼가 도착하기를 기다리는 상한 — 넘으면 기존 경로(바이너리 본문)로 받는다(보통 수 ms 안에 온다). */
const SHM_WAIT_MS = 3000;

/** WebView2 가 공유 버퍼와 함께 주는 이벤트(필요한 것만). */
interface SharedBufferEvent {
  additionalData?: Record<string, unknown> | null;
  getBuffer(): ArrayBuffer;
}

/** `window.chrome.webview` — Windows 통합앱(WebView2)에만 있다. 공유 버퍼에 필요한 것만 적는다. */
interface WebView2Bridge {
  addEventListener(type: typeof SHM_EVENT, fn: (e: SharedBufferEvent) => void): void;
  removeEventListener(type: typeof SHM_EVENT, fn: (e: SharedBufferEvent) => void): void;
  releaseBuffer(buffer: ArrayBuffer): void;
}

function webview2(): WebView2Bridge | null {
  const wv = (window as unknown as { chrome?: { webview?: Partial<WebView2Bridge> } }).chrome?.webview;
  return wv && typeof wv.addEventListener === 'function' && typeof wv.releaseBuffer === 'function' ? (wv as WebView2Bridge) : null;
}

/**
 * 받기 핸들에 **공유 버퍼**를 달라고 해서 받는다(설계서 §9.4 "받기 속도").
 *
 * 웹 → 네이티브 바이너리 본문은 WebView2 요청 본문을 타서 ~12 MiB/s 에서 막힌다(2026-09-25 실측). 공유 버퍼면 본문이 IPC 를 타지 않는다.
 * 없으면(macOS · 옛 WebView2 · 실패) null → 호출측이 기존 경로로 받는다. **받기를 막는 일은 없다.**
 * 🔴 이벤트 귀를 **명령보다 먼저** 연다 — 네이티브가 명령 응답보다 버퍼를 먼저 보낼 수 있다.
 */
async function requestSharedBuffer(handle: number): Promise<ArrayBuffer | null> {
  const wv = webview2();
  if (!wv) {
    return null;
  }
  let on: ((e: SharedBufferEvent) => void) | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const arrived = new Promise<ArrayBuffer | null>((resolve) => {
    on = (e) => {
      if (e.additionalData?.[SHM_META_KEY] === handle) {
        resolve(e.getBuffer());
      }
    };
    wv.addEventListener(SHM_EVENT, on);
    timer = setTimeout(() => resolve(null), SHM_WAIT_MS);
  });
  try {
    const ok = await call<boolean>(CMD.SINK_SHM, { handle }).catch(() => false);
    return ok ? await arrived : null;
  } finally {
    clearTimeout(timer);
    if (on) {
      wv.removeEventListener(SHM_EVENT, on);
    }
  }
}

/**
 * 네이티브가 알려 준 **보낼 목록**(`register_source` 의 응답).
 * 🔴 파일 패널의 폴더 목록(`protocol.ts` 의 `Listing`)과는 다른 것이다 — 이름이 겹치지 않게 `Source-` 를 붙였다.
 */
interface SourceListing {
  handle: number;
  entries: Entry[];
  /** 건너뛴 바로 가기·링크 수(따라가지 않는다 — 설계서 §9.4 보안). */
  skipped: number;
}

/** 끌어다 놓는 중 — 좌표는 CSS px(창 기준 = clientX/Y). */
export interface NativeDrag {
  over: boolean;
  x?: number;
  y?: number;
}

/** 놓았다 — 성공이면 목록·핸들, 실패(이름 규칙·상한 등)면 error. */
export interface NativeDrop extends Partial<SourceListing> {
  error?: { code: string; detail?: string };
  x: number;
  y: number;
}

/** 네이티브 오류(`{code, detail}`) → FileError(같은 코드 → 같은 사용자 문구). */
function toFileError(e: unknown, fallback: string = FILE_ERR.FAILED): FileError {
  if (e instanceof FileError) {
    return e;
  }
  const o = e as { code?: unknown; detail?: unknown } | null;
  if (o && typeof o.code === 'string') {
    return new FileError(o.code, typeof o.detail === 'string' ? o.detail : undefined);
  }
  return new FileError(fallback, String(e));
}

/** 네이티브 명령 호출(브라우저 번들에서 Tauri 모듈을 미리 싣지 않도록 동적 import — 다른 앱 전용 코드와 같은 방식). */
async function call<T>(
  cmd: string,
  args?: Record<string, unknown> | Uint8Array,
  options?: { headers: Record<string, string> },
  fallback?: string,
): Promise<T> {
  const { invoke } = await import('@tauri-apps/api/core');
  try {
    return await invoke<T>(cmd, args, options);
  } catch (e) {
    throw toFileError(e, fallback);
  }
}

/** 핸들을 버린다(결과는 기다리지 않는다 — 실패해도 창이 닫힐 때 네이티브가 정리한다). */
export function releaseNativeHandle(handle: number): void {
  void call(CMD.RELEASE, { handle }).catch(() => undefined);
}

/** 네이티브가 등록한 보낼 목록. 읽기는 핸들 + 순번으로만 된다(경로는 JS 에 없다). */
export class NativeFileSource implements FileSource {
  readonly entries: Entry[];
  readonly skipped: number;
  readonly blockSize = NATIVE_READ_BLOCK;
  private readonly handle: number;

  constructor(listing: SourceListing) {
    this.handle = listing.handle;
    this.entries = listing.entries;
    this.skipped = listing.skipped;
  }

  async read(i: number, offset: number, len: number): Promise<Uint8Array> {
    const buf = await call<ArrayBuffer>(CMD.READ, { handle: this.handle, i, offset, len }, undefined, FILE_ERR.LOCAL_READ);
    return new Uint8Array(buf);
  }

  dispose(): void {
    releaseNativeHandle(this.handle);
  }
}

/** Ctrl+V — 내 클립보드에 탐색기에서 복사한 **파일**이 있으면 출처, 없으면 null(텍스트·이미지는 클립보드 공유 경로로). */
export async function readNativeFileClipboard(): Promise<NativeFileSource | null> {
  const listing = await call<SourceListing | null>(CMD.CLIP_READ);
  return listing ? new NativeFileSource(listing) : null;
}

/** 끌어다 놓은 결과 → 출처(실패였으면 그 오류를 던진다). */
export function sourceFromDrop(d: NativeDrop): NativeFileSource {
  if (d.error || d.handle === undefined || !d.entries) {
    throw toFileError(d.error ?? { code: FILE_ERR.LOCAL_READ });
  }
  return new NativeFileSource({ handle: d.handle, entries: d.entries, skipped: d.skipped ?? 0 });
}

/**
 * 네이티브에 받는 공통부 — 시작·끝만 받을 곳마다 갈라지고, 조각을 넘기는 방식은 여기서 고른다:
 *  - **공유 버퍼**(Windows): 버퍼를 채울 때마다 `files_sink_commit {handle, i, len}` — 본문이 IPC 를 타지 않는다.
 *  - **바이너리 본문**(그 밖 · 버퍼를 못 받았을 때): 1 MiB 씩 모아 `files_sink_write`.
 * 받을 곳이 두 가지다: 앱 스테이징(Ctrl+C — 끝나면 내 파일 클립보드) · 파일 패널에서 **연 폴더**(그대로 둔다).
 */
abstract class NativeSinkBase implements FileSink {
  abstract readonly label: string;
  protected handle = 0;
  private readonly pending = new Map<number, { parts: Uint8Array[]; size: number }>();
  /** 공유 버퍼(없으면 바이너리 본문 경로). */
  private shm: { buffer: ArrayBuffer; view: Uint8Array } | null = null;
  /** 공유 버퍼에 채운 바이트 · 그 바이트가 속한 파일 순번(버퍼 하나는 한 파일의 이어진 조각만 담는다). */
  private shmFill = 0;
  private shmFile = -1;

  /** 받기 핸들을 연다(받을 곳마다 다른 명령). */
  protected abstract open(entries: Entry[]): Promise<number>;
  /** 전체 끝(받을 곳마다 다른 명령). */
  protected abstract finish(): Promise<void>;

  async begin(entries: Entry[]): Promise<void> {
    this.handle = await this.open(entries);
    const buffer = await requestSharedBuffer(this.handle);
    this.shm = buffer ? { buffer, view: new Uint8Array(buffer) } : null;
  }

  async complete(): Promise<void> {
    await this.finish();
    // 네이티브는 다 받으면 버퍼를 닫는다(웹 쪽 ArrayBuffer 도 비워진다) — 여기서도 놓는다.
    this.dropShm();
  }

  async write(i: number, data: Uint8Array<ArrayBuffer>): Promise<void> {
    if (this.shm) {
      await this.writeShm(this.shm.view, i, data);
      return;
    }
    let p = this.pending.get(i);
    if (!p) {
      p = { parts: [], size: 0 };
      this.pending.set(i, p);
    }
    p.parts.push(data);
    p.size += data.length;
    if (p.size >= FLUSH_BYTES) {
      await this.flush(i);
    }
  }

  /** 모아 둔 조각을 한 번에 넘긴다(바이너리 본문 — JSON·base64 로 부풀리지 않는다). */
  private async flush(i: number): Promise<void> {
    const p = this.pending.get(i);
    if (!p || p.size === 0) {
      return;
    }
    const merged = new Uint8Array(p.size);
    let at = 0;
    for (const part of p.parts) {
      merged.set(part, at);
      at += part.length;
    }
    p.parts = [];
    p.size = 0;
    await call(
      CMD.SINK_WRITE,
      merged,
      { headers: { [HDR_HANDLE]: String(this.handle), [HDR_INDEX]: String(i) } },
      FILE_ERR.LOCAL_WRITE,
    );
  }

  /**
   * 공유 버퍼에 채운다 — 가득 차면 네이티브에 쓰게 한다. 다른 파일의 조각이 오면 앞 파일 몫을 먼저 쓴다.
   * 🔴 채운 자리를 비우는 것은 **네이티브가 다 읽은 뒤**(commit 응답 뒤)다 — 그 전에 덮으면 읽는 도중 내용이 바뀐다.
   */
  private async writeShm(view: Uint8Array, i: number, data: Uint8Array): Promise<void> {
    if (this.shmFile !== i && this.shmFill > 0) {
      await this.commitShm();
    }
    this.shmFile = i;
    let at = 0;
    while (at < data.length) {
      const n = Math.min(data.length - at, view.length - this.shmFill);
      view.set(data.subarray(at, at + n), this.shmFill);
      this.shmFill += n;
      at += n;
      if (this.shmFill === view.length) {
        await this.commitShm();
      }
    }
  }

  /** 공유 버퍼에 채운 만큼 네이티브가 파일에 쓴다. */
  private async commitShm(): Promise<void> {
    if (this.shmFill === 0) {
      return;
    }
    await call(CMD.SINK_COMMIT, { handle: this.handle, i: this.shmFile, len: this.shmFill }, undefined, FILE_ERR.LOCAL_WRITE);
    this.shmFill = 0;
  }

  /** 공유 버퍼를 놓는다(끝·취소). 이미 네이티브가 닫았으면 빈 버퍼라 아무 일도 없다. */
  private dropShm(): void {
    if (this.shm) {
      try {
        webview2()?.releaseBuffer(this.shm.buffer);
      } catch {
        // 이미 닫힌 버퍼 — 놓을 것이 없다.
      }
    }
    this.shm = null;
    this.shmFill = 0;
    this.shmFile = -1;
  }

  async finishFile(i: number): Promise<void> {
    if (this.shm && this.shmFile === i) {
      await this.commitShm();
    }
    await this.flush(i);
    this.pending.delete(i);
    await call(CMD.SINK_FINISH, { handle: this.handle, i }, undefined, FILE_ERR.LOCAL_WRITE);
  }

  async abort(): Promise<void> {
    this.pending.clear();
    this.dropShm();
    if (this.handle) {
      // 덜 받은 것은 네이티브가 치운다 — 스테이징은 폴더째, **패널 목적지는 만든 항목만**(사용자 폴더를 지우지 않는다).
      await call(CMD.RELEASE, { handle: this.handle }).catch(() => undefined);
    }
  }
}

/**
 * 앱 스테이징에 받는다 → 끝나면 네이티브가 내 파일 클립보드(MOVE)에 올린다(`clipboardSet`).
 * 해시·순서는 채널이 확인한다. 받은 폴더는 `reveal()` 로 열어 보일 수 있다([폴더 열기]).
 */
export class NativeStagingSink extends NativeSinkBase {
  readonly label = '앱 받은 파일 폴더';
  /** 전부 받은 뒤 내 클립보드에 올렸는가(올리지 못했으면 [폴더 열기] 로 안내한다). */
  clipboardSet = false;

  protected open(entries: Entry[]): Promise<number> {
    return call<number>(CMD.SINK_BEGIN, { entries }, undefined, FILE_ERR.LOCAL_WRITE);
  }

  protected async finish(): Promise<void> {
    // 채널은 파일마다 finishFile 을 부른 뒤 complete 를 부른다 — 남은 조각이 있으면 형식 오류다(네이티브가 "덜 받음" 으로 거절한다).
    const r = await call<{ clipboard: boolean }>(CMD.SINK_COMPLETE, { handle: this.handle }, undefined, FILE_ERR.LOCAL_WRITE);
    this.clipboardSet = r.clipboard;
  }

  /**
   * 끌어내기 3단계(F5) — 손이 **아직 버튼을 누르고 있으면** 네이티브 끌기로 넘긴다(사용자가 놓은 곳에 떨어진다).
   * 놓을 때까지 돌아오지 않는다(모달). 이미 손을 뗐거나 이 OS 가 지원하지 않으면 false → 호출측이 알림으로 마무리한다.
   */
  async startDrag(): Promise<boolean> {
    if (!this.handle) {
      return false;
    }
    const r = await call<{ dragged: boolean }>(CMD.DRAG_START, { handle: this.handle }).catch(() => ({ dragged: false }));
    return !!r.dragged;
  }

  /** 받은 폴더를 탐색기·Finder 로 연다. */
  reveal(): void {
    if (this.handle) {
      void call(CMD.REVEAL, { handle: this.handle }).catch(() => undefined);
    }
  }
}

/** 파일 패널 — 내 PC 칸에서 **연 폴더**에 바로 받는다(클립보드에 올리지 않는다). */
class NativeDirSink extends NativeSinkBase {
  constructor(private readonly dir: string) {
    super();
  }

  get label(): string {
    return this.dir;
  }

  protected open(entries: Entry[]): Promise<number> {
    return call<number>(CMD.LOCAL_SINK_BEGIN, { dir: this.dir, entries }, undefined, FILE_ERR.LOCAL_WRITE);
  }

  protected async finish(): Promise<void> {
    await call(CMD.LOCAL_SINK_COMPLETE, { handle: this.handle }, undefined, FILE_ERR.LOCAL_WRITE);
  }
}

/**
 * 통합앱의 **내 PC 칸** — 네이티브가 경로로 연다(브라우저의 "고른 폴더 핸들" 칸과 같은 인터페이스).
 * 목록은 네이티브가 이름·크기·시각만 주고, 내용 읽기는 `source()` 가 받은 **핸들**로만 한다.
 */
export class NativeLocalPane implements LocalPane {
  private constructor(private listing: Listing) {}

  /** 사용자 홈부터(없으면 최상위 = 드라이브 목록). */
  static async openHome(): Promise<NativeLocalPane> {
    const home = await call<string | null>(CMD.LOCAL_HOME).catch(() => null);
    return NativeLocalPane.open(home ?? '');
  }

  static async open(dir: string): Promise<NativeLocalPane> {
    return new NativeLocalPane(parseListing(await call<Record<string, unknown>>(CMD.LOCAL_LIST, { dir })));
  }

  get label(): string {
    return this.listing.dir || '이 PC';
  }

  get canUp(): boolean {
    return this.listing.parent !== null;
  }

  /** 절대 경로(빈 문자열 = 최상위 드라이브 목록) — 칸 머리의 드라이브 선택이 지금 드라이브를 가린다. */
  get path(): string {
    return this.listing.dir;
  }

  /** 다시 읽는다(새로 고침·폴더 조작 뒤) — 화면이 낡은 목록을 그대로 두지 않게. */
  async list(): Promise<DirItem[]> {
    this.listing = parseListing(await call<Record<string, unknown>>(CMD.LOCAL_LIST, { dir: this.listing.dir }));
    return this.listing.items;
  }

  async open(name: string): Promise<LocalPane> {
    return NativeLocalPane.open(joinPath(this.listing.dir, name));
  }

  async up(): Promise<LocalPane | null> {
    return this.listing.parent === null ? null : NativeLocalPane.open(this.listing.parent);
  }

  async mkdir(name: string): Promise<void> {
    await call(CMD.LOCAL_MKDIR, { dir: this.listing.dir, name });
  }

  async remove(names: string[]): Promise<void> {
    await call(CMD.LOCAL_DELETE, { dir: this.listing.dir, names });
  }

  async source(names: string[]): Promise<FileSource> {
    return new NativeFileSource(await call<SourceListing>(CMD.LOCAL_SOURCE, { dir: this.listing.dir, names }));
  }

  sink(): FileSink {
    return new NativeDirSink(this.listing.dir);
  }
}

/**
 * 이 창의 끌어다 놓기 이벤트를 듣는다(세션 창 — 네이티브가 창 label 로 보낸다). 해제 함수를 돌려준다.
 * 💡 앱은 Tauri 기본 설정이라 웹뷰의 HTML5 파일 놓기(onDrop)가 오지 않는다 — 이것이 앱의 끌어다 놓기 경로다.
 */
export async function listenNativeFileDrops(
  onDrag: (d: NativeDrag) => void,
  onDrop: (d: NativeDrop) => void,
): Promise<() => void> {
  const { getCurrentWebviewWindow } = await import('@tauri-apps/api/webviewWindow');
  const w = getCurrentWebviewWindow();
  const offDrag = await w.listen<NativeDrag>(FILE_DRAG_EVENT, (e) => onDrag(e.payload));
  const offDrop = await w.listen<NativeDrop>(FILE_DROP_EVENT, (e) => onDrop(e.payload));
  return () => {
    offDrag();
    offDrop();
  };
}
